import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Check } from 'lucide-react';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

export const SearchSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Zoek...",
  displayKey = "label",
  valueKey = "value",
  renderOption,
  disabled = false,
  className,
  autoOpen = false  // Don't auto-open when there's already a value
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Find selected option
  const selectedOption = options.find(opt => 
    (typeof opt === 'object' ? opt[valueKey] : opt) === value
  );

  // Filter options based on search
  const filteredOptions = options.filter(opt => {
    const label = typeof opt === 'object' ? opt[displayKey] : opt;
    return label?.toLowerCase().includes(search.toLowerCase());
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    const val = typeof opt === 'object' ? opt[valueKey] : opt;
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  // Handle focus - only open if no value selected or autoOpen is true
  const handleFocus = () => {
    if (disabled) return;
    if (!value || autoOpen) {
      setIsOpen(true);
    }
  };

  const displayValue = selectedOption 
    ? (typeof selectedOption === 'object' ? selectedOption[displayKey] : selectedOption)
    : '';

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div 
        className={cn(
          "flex items-center border rounded-md bg-background cursor-pointer",
          "focus-within:ring-1 focus-within:ring-ring",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => !disabled && setIsOpen(true)}
      >
        <Search className="w-4 h-4 ml-3 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={isOpen ? search : displayValue}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Geen resultaten gevonden
            </div>
          ) : (
            filteredOptions.map((opt, i) => {
              const optValue = typeof opt === 'object' ? opt[valueKey] : opt;
              const optLabel = typeof opt === 'object' ? opt[displayKey] : opt;
              const isSelected = optValue === value;

              return (
                <div
                  key={optValue || i}
                  className={cn(
                    "px-3 py-2 cursor-pointer flex items-center justify-between",
                    "hover:bg-accent hover:text-accent-foreground",
                    isSelected && "bg-accent/50"
                  )}
                  onClick={() => handleSelect(opt)}
                >
                  {renderOption ? renderOption(opt) : (
                    <span className="text-sm">{optLabel}</span>
                  )}
                  {isSelected && <Check className="w-4 h-4 text-primary" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
